import React from "react";
import { useLang } from "@/contexts/LangContext";
import MotionReveal from "@/components/MotionReveal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MapPin } from "lucide-react";

const Travel = () => {
  const { t, dict } = useLang();
  return (
    <div data-testid="travel-page">
      <section className="mx-auto max-w-[1180px] px-5 sm:px-8 pt-24 sm:pt-32">
        <MotionReveal>
          <p className="editorial-kicker">{t("travel.overline")}</p>
        </MotionReveal>
        <MotionReveal delay={0.05}>
          <h1 className="editorial-h1 mt-6 text-4xl sm:text-6xl lg:text-7xl max-w-[18ch]">
            {t("travel.title")}
          </h1>
        </MotionReveal>
        <MotionReveal delay={0.1}>
          <p className="mt-8 text-foreground/75 max-w-[52ch] leading-relaxed">{t("travel.intro")}</p>
        </MotionReveal>
      </section>

      <section className="mx-auto max-w-[1180px] px-5 sm:px-8 mt-14 sm:mt-20" data-testid="travel-info">
        <Tabs defaultValue="flights" className="w-full">
          <TabsList className="bg-transparent border-b border-border w-full justify-start gap-2 sm:gap-6 h-auto p-0">
            <TabsTrigger
              value="flights"
              data-testid="travel-tab-flights"
              className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-foreground data-[state=active]:border-b data-[state=active]:border-accent rounded-none px-2 sm:px-3 pb-3 text-[11px] sm:text-[12px] tracking-[0.22em] uppercase text-foreground/45"
            >
              {dict.travel.tabs.flights}
            </TabsTrigger>
            <TabsTrigger
              value="stay"
              data-testid="travel-tab-stay"
              className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-foreground data-[state=active]:border-b data-[state=active]:border-accent rounded-none px-2 sm:px-3 pb-3 text-[11px] sm:text-[12px] tracking-[0.22em] uppercase text-foreground/45"
            >
              {dict.travel.tabs.stay}
            </TabsTrigger>
            <TabsTrigger
              value="transport"
              data-testid="travel-tab-transport"
              className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-foreground data-[state=active]:border-b data-[state=active]:border-accent rounded-none px-2 sm:px-3 pb-3 text-[11px] sm:text-[12px] tracking-[0.22em] uppercase text-foreground/45"
            >
              {dict.travel.tabs.transport}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="flights" className="mt-10 grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <h3 className="editorial-h2 text-3xl sm:text-4xl">{dict.travel.flights.title}</h3>
              <p className="editorial-kicker mt-4 text-accent">{dict.travel.flights.tip}</p>
            </div>
            <p className="lg:col-span-7 text-foreground/75 leading-relaxed max-w-[56ch]">
              {dict.travel.flights.body}
            </p>
          </TabsContent>

          <TabsContent value="stay" className="mt-10 grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <h3 className="editorial-h2 text-3xl sm:text-4xl">{dict.travel.stay.title}</h3>
              <p className="text-foreground/75 mt-4 leading-relaxed max-w-[44ch]">{dict.travel.stay.body}</p>
            </div>
            <div className="lg:col-span-7 grid gap-4">
              {dict.travel.stay.list.map((h) => (
                <article key={h.name} className="surface-card p-6">
                  <p className="font-serif-display text-2xl">{h.name}</p>
                  <p className="mt-2 text-foreground/65">{h.note}</p>
                </article>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="transport" className="mt-10 grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <h3 className="editorial-h2 text-3xl sm:text-4xl">{dict.travel.transport.title}</h3>
              <p className="editorial-kicker mt-4 text-accent">{dict.travel.transport.taxi}</p>
            </div>
            <p className="lg:col-span-7 text-foreground/75 leading-relaxed max-w-[56ch]">
              {dict.travel.transport.body}
            </p>
          </TabsContent>
        </Tabs>
      </section>

      <section className="mx-auto max-w-[1180px] px-5 sm:px-8 mt-20 sm:mt-28 pb-12">
        <MotionReveal>
          <div className="grid gap-6 sm:grid-cols-2">
            <article className="surface-card p-6">
              <p className="editorial-kicker text-accent">{dict.travel.addresses.title}</p>
              <a
                href="https://maps.app.goo.gl/bBN2GFpLR1yZ1dh99?g_st=ac"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="travel-venue-map-link"
                className="group inline-flex items-baseline gap-2 mt-3 font-serif-display text-2xl hover:text-accent transition-colors"
              >
                <span className="underline decoration-accent/40 underline-offset-4 group-hover:decoration-accent">
                  {dict.travel.addresses.venue}
                </span>
                <MapPin className="w-5 h-5 shrink-0 self-center text-accent" aria-hidden="true" />
              </a>
              <p className="mt-3 text-[11px] tracking-[0.22em] uppercase text-foreground/55 group-hover:text-accent">
                {t("schedule.mapOpen")}
              </p>
            </article>
            <article className="surface-card p-6">
              <p className="editorial-kicker text-accent">Contact</p>
              <p className="font-serif-display text-xl mt-3 break-words">
                {(() => {
                  const email = "eliandmag@gmail.com";
                  const [before = "", after = ""] = dict.travel.addresses.contact.split(email);
                  return (
                    <>
                      {before}
                      <a
                        href={`mailto:${email}`}
                        data-testid="contact-email-link"
                        className="underline decoration-accent/50 underline-offset-4 hover:text-accent transition-colors"
                      >
                        {email}
                      </a>
                      {after}
                    </>
                  );
                })()}
              </p>
            </article>
          </div>
        </MotionReveal>
      </section>
    </div>
  );
};

export default Travel;
